import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl'
};

export function Modal({ open, onClose, title, description, children, footer, size = 'md', dismissable = true }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (dismissable && e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, dismissable]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div
        className="animate-fade-in absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
        onClick={dismissable ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        className={clsx(
          'animate-zoom-in relative z-10 flex w-full flex-col overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-elevated',
          SIZES[size]
        )}
      >
        {(title || description) ? (
          <header className="flex items-start gap-4 border-b border-border px-6 pb-4 pt-5">
            <div className="min-w-0 flex-1">
              {title && (
                <h2 id="modal-title" className="text-base font-semibold tracking-tight text-ink">
                  {title}
                </h2>
              )}
              {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
            </div>
            {dismissable && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Chiudi"
                className="-mr-1 -mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink"
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            )}
          </header>
        ) : (
          // No title/description (e.g. ConfirmDialog): skip the empty header band
          // and float the close button over the content so there is no blank
          // strip at the top of the dialog.
          dismissable && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="absolute right-3 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink"
            >
              <X className="h-4 w-4" strokeWidth={1.75} />
            </button>
          )
        )}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-border bg-surface px-6 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
}
